<template>
  <BaseTopBar
    v-if="isMounted"
    v-hotkey="shortcuts"
    :tint="tint"
    :left-panel-width="leftPanelWidth"
    :is-navigation-displayed="isNavigationDisplayed"
    :panel="panel"
    is-hideable
    with-find
    @find="$emit('find')"
  >
    <template #center>
      <div v-if="person.archived">
        <span>This person is archived.</span>
      </div>
    </template>
    <template #right>
      <RecordingToolbar
        v-if="showRecordingToolbar"
        v-bind="recordingToolbarProps"
        @recording-control-click="onRecordingControlClick"
        @toggle-transcript="toggleTranscriptPopover"
        @close-transcript="closeRecordingTranscript"
      />
      <ShareButton :export-action="onExport" :tint="tint" />
      <!-- Main Button -->
      <BaseButton
        v-if="!self && !person.archived"
        kind="auxiliary"
        :text="getActionText(mainAction)"
        :state="getActionState(mainAction)"
        :tint="tint"
        @click="getActionFunc(mainAction, $event)"
      >
        <template #icon>
          <component :is="mainAction.icon" />
        </template>
      </BaseButton>
    </template>
    <template #more>
      <BaseButton
        v-for="action in moreActions"
        :key="action.text"
        class="context-action"
        kind="auxiliary"
        :text="getActionText(action)"
        :state="getActionState(action)"
        :tint="action.tint || 'none'"
        is-expanded
        @click="getActionFunc(action, $event)"
      >
        <template #icon> <component :is="action.icon" /> </template
        ><template #right>
          {{ action.shortcutsDisplay }}
        </template>
      </BaseButton>
    </template>
  </BaseTopBar>
</template>
<script lang="ts">
import {
  computed,
  defineComponent,
  onMounted,
  PropType,
  ref,
  toRefs,
} from "vue";

import { People } from "@ctrl/controller-rpc";

import {
  eventActions,
  pageActions,
  peopleActions,
} from "@/components/actions/actions";
import BaseButton from "@/components/base/BaseButton.vue";
import BaseTopBar from "@/components/base/BaseTopBar.vue";
import { Panel, Tint } from "@/components/base/types";
import { PeopleContextActions } from "@/components/context-menu/context-actions";
import { formatRelativeDate } from "@/components/dates";
import * as inject from "@/components/inject";
import { createBuiltinExportAction } from "@/components/modals/export";
import { getPeopleShortcuts } from "@/components/object-shortcuts";
import RecordingToolbar from "@/components/RecordingToolbar.vue";
import ShareButton from "@/components/ShareButton.vue";
import {
  assignShortcuts,
  selectAll as selectAllShortcut,
  Shortcuts,
} from "@/components/shortcuts";
import { useAudioRecording } from "@/composables/recording/useAudioRecording";
import { useMeetingRecording } from "@/composables/recording/useMeetingRecording";
import { useRecordingToolbar } from "@/composables/recording/useRecordingToolbar";
import type { MeetingRecordingContext } from "@/recording/types";
import { useReleaser } from "@/ref";

enum InviteStatus {
  NotInvited,
  Inviting,
  Invited,
}
export default defineComponent({
  components: {
    BaseButton,
    BaseTopBar,
    RecordingToolbar,
    ShareButton,
  },
  props: {
    hasBlockActive: { type: Boolean, required: true },
    isNavigationDisplayed: { type: Boolean, default: true },
    leftPanelWidth: { type: Number, default: 0 },
    person: { type: Object as PropType<People>, required: true },
    withRightPanel: { type: Boolean, default: false },
  },
  emits: {
    find: () => true,
    flush: () => true,
    selectAll: () => true,
  },
  async setup(props, { emit }) {
    const billing = inject.billing();
    const controller = inject.controller();
    const logger = inject.logger();
    const profile = inject.profile();
    const router = inject.router();
    const isInputFocused = inject.isInputFocused();
    const isModalShown = inject.isModalShown();
    const menu = inject.menu();
    const palette = inject.palette();
    const automationHotkeys = inject.automationHotkeys();
    const flush = () => emit("flush");
    const selectAll = () => emit("selectAll");
    const { person, hasBlockActive, withRightPanel } = toRefs(props);
    const panel = computed<Panel>(() =>
      withRightPanel.value ? Panel.rightPanel : Panel.noPanel
    );
    // FIXME: Mandatory due to BaseTopBar teleportation
    const isMounted = ref(false);
    onMounted(() => (isMounted.value = true));
    const releaser = useReleaser();

    // Automation hotkeys run on the object being viewed: arm them with this
    // person while the page is mounted (same input as Mod+K → Run
    // automation).
    const deactivateAutomationHotkeys = automationHotkeys.value?.activate(
      () => ({
        kind: "reference",
        referenceId: { kind: "people", id: person.value.id },
      })
    );
    if (deactivateAutomationHotkeys)
      releaser.defer(deactivateAutomationHotkeys);

    // Stop/transcript controls for a meeting recording targeting this person
    // (started via the notes editor's /meet command). Shown only while this
    // person owns the active recording — mirrors CustomObjectTopBar.
    const meetingEvent = computed<MeetingRecordingContext>(() => ({
      id: person.value.id,
      title: (person.value.name?.display ?? person.value.email).trim(),
      target: { kind: "people" },
    }));
    const audioRecording = releaser.add(useAudioRecording(logger));
    const meetingRecording = useMeetingRecording(
      logger,
      billing,
      audioRecording,
      meetingEvent
    );
    const showRecordingToolbar = computed(
      () => !!person.value.id && meetingRecording.isRecordingHere.value
    );
    const {
      release: releaseRecordingToolbar,
      recordingToolbarProps,
      onRecordingControlClick,
      toggleTranscriptPopover,
      closeRecordingTranscript,
    } = useRecordingToolbar(meetingRecording, audioRecording, {
      toolbar: {
        compact: true,
        tint: "red",
        getTranscriptResetKey: () => person.value.id,
      },
    });
    releaser.defer(releaseRecordingToolbar);

    const profiles = await releaser.add(controller.auth.profiles());
    const self = computed<boolean>(
      () => !!profiles.value.find((x) => x.email === person.value.email)
    );
    const tint = computed<Tint>(() => (person.value.archived ? "red" : "none"));
    const inviteStatus = ref(InviteStatus.NotInvited);
    const inviteState = computed(() => {
      switch (+inviteStatus.value) {
        case InviteStatus.Inviting:
          return "loading";
        case InviteStatus.Invited:
          return "disabled";
        default:
          return "default";
      }
    });
    const inviteText = computed(() => {
      switch (+inviteStatus.value) {
        case InviteStatus.Inviting:
          return "Inviting…";
        case InviteStatus.Invited:
          return "Invite Sent";
        default:
          return "Invite";
      }
    });
    const mainAction = computed<PeopleContextActions>(
      () => peopleActions.invite
    );
    const isPinned = computed(() => {
      // Explicitly depend on menu.visibleItems.value to ensure reactivity
      menu.visibleItems.value;
      return menu.isItemPinned(person.value.id);
    });
    const moreActions = computed<PeopleContextActions[]>(() => {
      return [
        {
          ...peopleActions.togglePin,
          group: "bottom" as const,
          text: isPinned.value ? "Unpin" : "Pin",
        },
        peopleActions.runAutomation,
        peopleActions.copyLink,
      ];
    });
    const getActionFunc = (
      action: PeopleContextActions,
      mouseEvent?: MouseEvent
    ) =>
      action.action(
        { type: "people", value: person.value },
        {
          controller,
          router,
          menu,
          palette,
          clickEvent: mouseEvent,
        },
        action === peopleActions.invite
          ? {
              beforeAction: async () => {
                inviteStatus.value = InviteStatus.Inviting;
              },
              resolvedAction: () => (inviteStatus.value = InviteStatus.Invited),
            }
          : undefined
      );
    const getActionText = (action: PeopleContextActions) =>
      action === peopleActions.invite ? inviteText.value : action.text;
    const getActionState = (action: PeopleContextActions) =>
      action === peopleActions.invite ? inviteState.value : "default";
    const onExport = createBuiltinExportAction(controller, profile, () => ({
      title: person.value.name?.display ?? "Person",
      notes: person.value.notes,
      extensionKind: "people",
      extensionRow: person.value.extension_row,
    }));

    const shortcuts = computed<Shortcuts>(() => {
      const injection = { isModalShown, isInputFocused };
      const peopleShortcuts = self.value
        ? {}
        : getPeopleShortcuts(
            person.value,
            { controller, profile, router, menu, palette, ...injection },
            {
              flush,
            }
          );
      const base = hasBlockActive.value
        ? {}
        : {
            ...peopleShortcuts,
            ...assignShortcuts(injection, selectAllShortcut, () => {
              selectAll();
            }),
            ...assignShortcuts(
              injection,
              peopleActions.runAutomation.shortcuts,
              () => {
                getActionFunc(peopleActions.runAutomation);
              }
            ),
          };
      // Mod+F (find) is bound by BaseTopBar itself (`with-find`).
      return base;
    });
    return {
      eventActions,
      flush,
      formatRelativeDate,
      getActionFunc,
      getActionState,
      getActionText,
      isMounted,
      mainAction,
      moreActions,
      onExport,
      open,
      panel,
      self,
      shortcuts,
      tint,
      showRecordingToolbar,
      recordingToolbarProps,
      onRecordingControlClick,
      toggleTranscriptPopover,
      closeRecordingTranscript,
    };
  },
  computed: {
    pageActions() {
      return pageActions;
    },
  },
});
</script>
<style lang="scss" scoped>
@use "@/styles/context-actions.scss";
</style>
